import {
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  Show,
} from 'solid-js';
import type { JSX } from 'solid-js';
import {
  PERSISTENCE_KEY,
  useApiServices,
  useCapabilityRegistryContext,
  useCustomPropertiesMutationAllowed,
  useCustomPropertySets,
  useProjectSettings,
} from '@mobile-mapping/providers';
import { useConnectProject } from '@trimble/connect-project-providers';
import { t } from '@trimble/i18n';
import { asError } from '@trimble/logging';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { useLogger } from '@trimble/solidjs-utils';
import { ObjectPropertiesPanelCapability } from '@trimble/web3d-capabilities';
import { ObjectPropertiesPanel } from '@trimble/ui-components/solidjs';
import { isAbortError } from '@trimble/utilities';
import { makePersisted } from '@solid-primitives/storage';

export const ObjectPropertiesTab = (props: {
  onClose?(): void;
}): JSX.Element => {
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const { formattingSettings } = useProjectSettings();
  const { tcTcpsApi, getPsetApi } = useApiServices();
  const {
    selectedProjectId,
    selectedRegion,
    selectedProjectPropertiesLibraryUrl,
  } = useConnectProject();
  const logger = useLogger().getLogger('ObjectPropertiesTab');
  const customPropertiesMutationAllowed = useCustomPropertiesMutationAllowed();

  const currentUser = tcTcpsApi.getCurrentUser();
  const currentUserId = createMemo(() => currentUser.data?.tiduuid);

  const objectPropertiesPanelCapability = capabilityRegistry.get(
    ObjectPropertiesPanelCapability
  );

  const [propertyPanelSortAlphabetically, setPropertyPanelSortAlphabetically] =
    makePersisted(
      // eslint-disable-next-line solid/reactivity
      createSignal<boolean>(false),
      {
        name: PERSISTENCE_KEY.PropertyPanelSortAlphabetically,
        deserialize: (value: string) => {
          const parsed = JSON.parse(value) as unknown;
          return typeof parsed === 'boolean' ? parsed : false;
        },
      }
    );

  const [propertyPanelRefineNames, setPropertyPanelRefineNames] = makePersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<boolean>(false),
    {
      name: PERSISTENCE_KEY.PropertyPanelRefineNames,
      deserialize: (value: string) => {
        const parsed = JSON.parse(value) as unknown;
        return typeof parsed === 'boolean' ? parsed : false;
      },
    }
  );

  const [propertyPanelExpandedRows, setPropertyPanelExpandedRows] =
    makePersisted(
      // eslint-disable-next-line solid/reactivity
      createSignal<string[]>([]),
      {
        name: PERSISTENCE_KEY.PropertyPanelExpandedRows,
        deserialize: (value: string) => {
          const parsed = JSON.parse(value) as unknown;
          return Array.isArray(parsed)
            ? parsed.filter((row): row is string => typeof row === 'string')
            : [];
        },
      }
    );

  const [propertyPanelFavoriteKeys, setPropertyPanelFavoriteKeys] =
    createSignal<string[]>([]);

  const selectionLinks = createMemo(
    () => objectPropertiesPanelCapability()?.data()?.entities ?? []
  );

  // The accessor is consumed inside tracked scopes within the hook (createMemo
  // and createEffect), so the lint's usual "reactive variable escapes" warning
  // does not apply here.
  // eslint-disable-next-line solid/reactivity
  const customPropertySets = useCustomPropertySets(selectionLinks);
  const { customEntityPropertySets, isLoading: isCustomPropertiesLoading } =
    customPropertySets;

  const loadFavoritePropertyKeys = (signal?: AbortSignal): void => {
    const projectId = selectedProjectId();
    const userId = currentUserId();
    const region = selectedRegion();
    if (
      projectId === undefined ||
      userId === undefined ||
      region === undefined
    ) {
      setPropertyPanelFavoriteKeys([]);
      return;
    }

    void getPsetApi(region)
      .then((psetApi) =>
        psetApi.getFavoritePropertyKeys({
          projectId,
          userId,
          signal,
        })
      )
      .then((favoriteKeys) => {
        if (signal?.aborted) return;
        setPropertyPanelFavoriteKeys(favoriteKeys);
      })
      .catch((error: unknown) => {
        if (signal?.aborted || isAbortError(error)) {
          return;
        }
        logger.error(
          'Failed to load favorite property keys',
          { projectId, userId },
          asError(error)
        );
        NotificationEventBus.emit({
          type: 'error',
          message: t('objectProperties.loadFavoritesFailed'),
        });
        setPropertyPanelFavoriteKeys([]);
      });
  };

  createEffect(() => {
    const controller = new AbortController();
    onCleanup(() => controller.abort());
    loadFavoritePropertyKeys(controller.signal);
  });

  const onManagePropertySetLibraries = (): void => {
    const url = selectedProjectPropertiesLibraryUrl();
    if (url === undefined) return;

    window.open(url, '_blank');
  };

  const updateFavoritePropertyKeys = (favoriteKeys: string[]): void => {
    const projectId = selectedProjectId();
    const userId = currentUserId();
    const region = selectedRegion();
    if (
      projectId === undefined ||
      userId === undefined ||
      region === undefined
    ) {
      return;
    }

    // Optimistically update the input (drives the star fill). Capture only this
    // call's delta so a failed save can be reverted against the latest state,
    // rather than replacing it with a stale snapshot that would clobber an
    // overlapping save.
    const previousFavoriteKeys = propertyPanelFavoriteKeys();
    const added = favoriteKeys.filter(
      (key) => !previousFavoriteKeys.includes(key)
    );
    const removed = previousFavoriteKeys.filter(
      (key) => !favoriteKeys.includes(key)
    );
    setPropertyPanelFavoriteKeys(favoriteKeys);

    void getPsetApi(region)
      .then((psetApi) =>
        psetApi.updateFavoritePropertyKeys({
          projectId,
          userId,
          favoritePropertyKeys: favoriteKeys,
        })
      )
      .catch((error: unknown) => {
        setPropertyPanelFavoriteKeys((current) => {
          const reverted = new Set(current);
          for (const key of added) reverted.delete(key);
          for (const key of removed) reverted.add(key);
          return [...reverted];
        });
        logger.error(
          'Failed to save favorite property keys',
          { projectId, userId },
          asError(error)
        );
        NotificationEventBus.emit({
          type: 'error',
          message: t('objectProperties.saveFavoritesFailed'),
        });
      });
  };

  return (
    <Show when={objectPropertiesPanelCapability()} keyed>
      {(capability) => (
        <ObjectPropertiesPanel
          capability={capability}
          formattingSettings={formattingSettings}
          sortAlphabetically={propertyPanelSortAlphabetically()}
          refineNames={propertyPanelRefineNames()}
          onSortAlphabeticallyChanged={setPropertyPanelSortAlphabetically}
          onRefineNamesChanged={setPropertyPanelRefineNames}
          expandedRows={propertyPanelExpandedRows()}
          onExpandedRowsChange={setPropertyPanelExpandedRows}
          favoritePropertyKeys={propertyPanelFavoriteKeys()}
          onFavoritePropertyKeysChange={updateFavoritePropertyKeys}
          customEntityPropertySets={customEntityPropertySets()}
          customPropertiesMutationAllowed={customPropertiesMutationAllowed()}
          onManagePropertySetLibraries={onManagePropertySetLibraries}
          isLoading={capability.isLoading() || isCustomPropertiesLoading()}
          onClose={() => props?.onClose?.()}
        />
      )}
    </Show>
  );
};
